import { type ReactNode, useEffect, useRef } from "react";
import { tipsWaiting, waitsOnRating } from "../../src/waits.ts";
import type { Item, Link, Note, Pad, PadEntry, Research, TripView } from "./api.ts";
import { examplePrompt } from "./ask-ai.ts";
import { itemKind, KINDS, kindClass, kindOf, recommenders, waitingSuggestion } from "./format.ts";
import { ItemLinks } from "./ItemLinks.tsx";
import { useFolds, useKeptFirst, useKeptScroll } from "./kept.ts";
import { LinkCard } from "./LinkCard.tsx";
import { litProps, useLastOpened } from "./opened.ts";
import { ItemThumb } from "./Photos.tsx";
import { ratingWaits } from "./plan.ts";
import { RateMine, WhoElse } from "./Rate.tsx";
import { ResearchTile } from "./Research.tsx";
import { useReadOnly } from "./readOnly.ts";
import { keptKey } from "./reopen.ts";
import { SaveLink } from "./SaveLink.tsx";
import { Button, Chip, Icon } from "./souvenir.ts";
import { suggestionLine, suggestionsByKind } from "./suggestions.ts";
import { FirstVisit, useFirstVisitOpen } from "./Tips.tsx";
import { CopyLink, Empty, KindFold, PageSection, WaitingCard } from "./ui.tsx";
import { useViewing } from "./viewing.ts";

// The places and goals being considered and what's been learned, what waits on me first (DESIGN.md, Views,
// Ideas): what waits on my rating (Waiting for you, with Start rating), suggestions, from friends, research and
// links not about a place yet. Where we sleep and how we get there, a choice's options, are decided on
// Bookings, under their choice's row (since 2026-10-23: Options.tsx).

interface Props {
  trip: TripView;
  items: Item[];
  links: Link[];
  pads: Pad[];
  entries: PadEntry[];
  research: Research[];
  notes: Note[];
  onOpen: (id: string) => void;
  // A research tile: the finding, to be read in full over Ideas.
  onRead: (id: string) => void;
  // After I write a note here: load the trip again.
  onChanged: () => void;
  // Waiting for you's Start rating: the deck, closing back here.
  onStartRating: () => void;
  // Its Add (AskAi.tsx) beside its title, on a computer; on a phone it floats above the tabs.
  add?: ReactNode;
  // Save a link, chosen from Add: its section opens and its field takes the focus.
  start?: boolean;
  onStarted?: () => void;
}

export function Ideas({
  trip,
  items,
  links,
  pads,
  entries,
  research,
  notes,
  onOpen,
  onRead,
  onChanged,
  onStartRating,
  add = null,
  start = false,
  onStarted,
}: Props) {
  // The sections ("ideas:<section>", below) and suggestions' kinds ("suggestions:<kind>"; all rated, they
  // start folded), as I last left them on this device.
  const folds = useFolds(keptKey("folds", trip.you, trip.id));
  const page = useRef<HTMLElement>(null);
  const tipOpen = useFirstVisitOpen("ideas");
  useKeptScroll(page, "ideas");
  // The last thing opened from here, lit.
  const last = useLastOpened();
  const inbox = links.filter((l) => l.items.length === 0);
  // Places and goals: a choice's options are compared, rated and counted on Bookings (DESIGN.md, Views,
  // Bookings, 2026-10-23), never here.
  const choiceIds = new Set(items.filter((i) => i.type === "choice").map((i) => i.id));
  const ideas = items.filter((i) => !(i.parent && choiceIds.has(i.parent)));
  // Every place and goal still being considered, whoever added it, but friends' picks (suggestions.ts).
  const suggestions = suggestionsByKind(items, trip.members, trip.you);
  const suggested = suggestions.reduce((n, g) => n + g.items.length, 0);
  // A read-only trip takes no ratings: nothing waits on mine, and nothing is rated here.
  const readOnly = useReadOnly();
  // A shared trip's view (features/sharing.md): its places, research and links; friends only with their switch.
  const viewing = useViewing();
  const toRate = (list: Item[]) => (readOnly ? 0 : list.filter((i) => waitsOnRating(i, trip.you)).length);
  // What waits on my rating here (src/waits.ts, the one rule; plan.ts splits off a choice's options).
  const waiting = readOnly ? [] : ratingWaits(items, trip.you).ideas;
  // The kinds that had some I hadn't rated when this page opened: they stay open as I rate the last of
  // them, rather than folding under my finger; next time the page opens, they start folded.
  const waitedOnOpen = useKeptFirst("ideas-suggest-waiting", () =>
    suggestions.filter((g) => toRate(g.items) > 0).map((g) => g.kind.key as string),
  );
  const byId = new Map(items.map((i) => [i.id, i]));
  // Friends: those who recommend something in the plan, and those with a pad; one name, one friend.
  // Most picks first. A pick that is one of a choice's options shows on Bookings with its choice, not
  // here, so a hotel shows in one place (DESIGN.md, Views, Ideas, From friends).
  const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
  const picksBy = (f: string) => ideas.filter((i) => recommenders(i).some((r) => same(r, f)));
  const friends: string[] = [];
  for (const name of [...ideas.flatMap(recommenders), ...pads.map((p) => p.for)])
    if (!friends.some((f) => same(f, name))) friends.push(name);
  const picksOf = (f: string) => picksBy(f).length;
  friends.sort((a, b) => picksOf(b) - picksOf(a) || a.localeCompare(b));
  // Each section folds by its heading, as I last left it on this device: open until I fold it, but
  // Research, which waits on nobody, folded until I open it (DESIGN.md, Views, Ideas).
  const section = (name: string, foldedFirst = false) => {
    const key = `ideas:${name}`;
    const open = !folds.folded(key, foldedFirst);
    return { open, onToggle: () => folds.set(key, open) };
  };
  const suggestedFold = section("suggestions");
  const friendsFold = section("friends");
  // Research folded at first only with findings in it: empty, it says so, as every empty section does.
  const researchFold = section("research", research.length > 0);
  const inboxFold = section("links");
  // biome-ignore lint/correctness/useExhaustiveDependencies: once each time Save a link is chosen
  useEffect(() => {
    if (!start) return;
    if (!inboxFold.open) inboxFold.onToggle();
    const frame = requestAnimationFrame(() => {
      const field = page.current?.querySelector<HTMLElement>('[data-edit="save-link"]');
      field?.scrollIntoView({ block: "center" });
      field?.focus({ preventScroll: true });
      onStarted?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [start]);
  return (
    <section className="tm-options" aria-labelledby="ideas-title" ref={page}>
      <header className="tm-options-head">
        <div className="tm-view-title">
          <h1 id="ideas-title" className="display-m">
            Ideas
          </h1>
          {add}
        </div>
        {/* While the first-visit card shows, it stands in for this line. */}
        {!tipOpen && (
          <p className="body tm-muted">
            {viewing
              ? `The places and goals they're considering and what they've learned: suggestions${viewing.shares.has("recommendations") ? ", friends' tips" : ""}${viewing.shares.has("research") ? ", research and links" : ""}.`
              : "The places and goals we're considering and what we've learned: suggestions, friends' tips, research and links."}
          </p>
        )}
      </header>
      <FirstVisit view="ideas" />
      {waiting.length > 0 && <WaitingForYou waiting={waiting} onStart={onStartRating} />}
      <PageSection
        id="suggested-title"
        title="Suggestions"
        count={suggested}
        caption={`Places and goals anyone in the trip or their AI came up with, until they're in the plan or dropped.${
          readOnly ? "" : " Rate them here: the ones you haven't rated come first."
        }`}
        {...suggestedFold}
      >
        {suggested === 0 && (
          <Empty prompt={examplePrompt(trip.title, "ideas")} readOnly="No suggestions.">
            No suggestions yet. Places and goals anyone in the trip or their AI comes up with wait here for everyone to
            rate.
          </Empty>
        )}
        {suggestions.map(({ kind, items: list }) => {
          const left = toRate(list);
          const fold = `suggestions:${kind.key}`;
          const id = `suggested-${kind.key}`;
          // One kind folding like a choice: how many, and how many I have left to rate.
          return (
            <KindFold
              key={kind.key}
              id={id}
              kind={kind.key}
              icon={kind.icon}
              label={kind.label}
              count={list.length}
              chip={
                readOnly ? null : left > 0 ? (
                  <Chip icon="star">{`${left} to rate`}</Chip>
                ) : (
                  <Chip icon="check" tone="moss">
                    All rated
                  </Chip>
                )
              }
              folded={folds.folded(fold, left === 0 && !waitedOnOpen.includes(kind.key))}
              onFold={(folded) => folds.set(fold, folded)}
            >
              <ul className="tm-rows tm-suggestions">
                {list.map((i) => (
                  <li key={i.id} className="tm-suggestion">
                    <button
                      type="button"
                      className={`tm-row${i.id === last ? ` is-lit ${kindClass(kindOf(i))}` : ""}`}
                      {...litProps(i.id === last)}
                      onClick={() => onOpen(i.id)}
                    >
                      <ItemThumb item={i} trip={trip.id} suggested={waitingSuggestion(i, trip.members)} />
                      <span className="tm-row-text">
                        <span className="callout">{i.title}</span>
                        <span className="caption tm-muted">
                          {suggestionLine(i, trip.members, trip.you)} · {itemKind(i)}
                        </span>
                      </span>
                    </button>
                    <div className="tm-suggestion-say">
                      <WhoElse item={i} members={trip.members} you={trip.you} />
                      <RateMine item={i} trip={trip.id} you={trip.you} onChanged={onChanged} />
                    </div>
                  </li>
                ))}
              </ul>
            </KindFold>
          );
        })}
      </PageSection>
      {(!viewing || viewing.shares.has("recommendations")) && (
        <PageSection
          id="friends-title"
          title="From friends"
          count={friends.length}
          caption={`What friends recommend, and the pages they write in without an account.${
            readOnly ? "" : " Your AIs bring their tips into the plan."
          }`}
          {...friendsFold}
        >
          {friends.length === 0 ? (
            <Empty prompt={examplePrompt(trip.title, "friends")} readOnly="Nothing from friends.">
              Nothing from friends yet. Tell your AI what a friend recommends, or ask it for a page your friend can
              write in without an account.
            </Empty>
          ) : (
            <div className="tm-pad-grid">
              {friends.map((f) => (
                <FriendCard
                  key={f}
                  name={f}
                  picks={picksBy(f)}
                  pads={pads.filter((p) => same(p.for, f))}
                  entries={entries.filter((e) => pads.some((p) => p.id === e.pad && same(p.for, f)))}
                  links={links}
                  trip={trip}
                  onChanged={onChanged}
                  onOpen={onOpen}
                />
              ))}
            </div>
          )}
        </PageSection>
      )}
      {(!viewing || viewing.shares.has("research")) && (
        <PageSection
          id="research-title"
          title="Research"
          count={research.length}
          caption={
            viewing
              ? "What their AIs found out, with where it came from."
              : "What your AIs found out, with where it came from."
          }
          {...researchFold}
        >
          {research.length === 0 ? (
            <Empty prompt={examplePrompt(trip.title, "research")} readOnly="Nothing researched.">
              Nothing researched yet. What your AI finds out for the trip is kept here, with where it came from.
            </Empty>
          ) : (
            <div className="tm-research-list">
              {research.map((r) => (
                <ResearchTile
                  key={r.id}
                  finding={{
                    research: r,
                    about: r.about.flatMap((id) => byId.get(id) ?? []),
                    notes: notes.filter((n) => n.on === r.id),
                  }}
                  trip={trip}
                  lit={r.id === last}
                  onRead={onRead}
                />
              ))}
            </div>
          )}
        </PageSection>
      )}
      {(inbox.length > 0 || !readOnly) && (
        <PageSection
          id="inbox-title"
          title="Links"
          count={inbox.length}
          caption={
            readOnly
              ? "Not about a place yet."
              : "Not about a place yet. Save one here, or share it to Tripmo from your phone; say what each is about with About, or ask your AI to bring in what's in your links inbox."
          }
          {...inboxFold}
        >
          {!readOnly && <SaveLink trip={trip.id} onChanged={onChanged} />}
          {inbox.length > 0 && (
            <div className="tm-link-grid">
              {inbox.map((l) => (
                <LinkCard key={l.id} link={l} trip={trip.id} items={items} onChanged={onChanged} />
              ))}
            </div>
          )}
        </PageSection>
      )}
    </section>
  );
}

// Waiting for you, first on Ideas while anything waits on my rating (DESIGN.md, Views, "What waits on me
// leads"): how many, by kind, and Start rating, which opens the deck (Deck.tsx).
function WaitingForYou({ waiting, onStart }: { waiting: Item[]; onStart: () => void }) {
  const kinds = KINDS.map((kind) => ({ kind, n: waiting.filter((i) => kindOf(i) === kind.key).length })).filter(
    (k) => k.n > 0,
  );
  const n = waiting.length;
  return (
    <WaitingCard
      count={n}
      line={
        <>
          {n === 1 ? "One idea waits" : `${n} ideas wait`} for your rating, so your group, and your AI, know what
          matters to you.
        </>
      }
      action={
        <Button variant="primary" icon="star" className="tm-waiting-start" onClick={onStart}>
          Start rating
        </Button>
      }
    >
      <ul className="tm-waiting-kinds" aria-label="By kind">
        {kinds.map(({ kind, n }) => (
          <li key={kind.key} className={`tm-waiting-kind ${kindClass(kind.key)}`}>
            <Icon name={kind.icon} size={14} className="tm-waiting-icon" />
            {kind.label}
            <span className="tm-waiting-n">{n}</span>
          </li>
        ))}
      </ul>
    </WaitingCard>
  );
}

// A friend's pad: its link to send them, and what they've written, new or already in the plan.
// One friend: their picks in the plan (each opening its card), then their pads, with the link to
// send and what they wrote (new until an AI brings it in).
function FriendCard(props: {
  name: string;
  picks: Item[];
  pads: Pad[];
  entries: PadEntry[];
  links: Link[];
  trip: TripView;
  onChanged: () => void;
  onOpen: (id: string) => void;
}) {
  const { name, picks, pads, entries, links, trip, onChanged, onOpen } = props;
  const last = useLastOpened();
  // None counts as new on a read-only trip (each tip still says it's new from them).
  const waiting = tipsWaiting(entries, useReadOnly());
  return (
    <article className="tm-option tm-friend" aria-label={name}>
      <div className="tm-option-top">
        <span className="tm-row-icon is-pick">
          <Icon name="heart" size={16} />
        </span>
        <span className="tm-option-title body-strong">{name}</span>
        {waiting > 0 && <Chip tone="sun">{`${waiting} new`}</Chip>}
      </div>
      {picks.length > 0 ? (
        <ul className="tm-rows" aria-label={`${name}'s picks`}>
          {picks.map((i) => {
            const said = i.recommended_by?.find((r) => r.name === name)?.said;
            return (
              <li key={i.id}>
                <button
                  type="button"
                  className={`tm-row${i.id === last ? ` is-lit ${kindClass(kindOf(i))}` : ""}`}
                  {...litProps(i.id === last)}
                  onClick={() => onOpen(i.id)}
                >
                  <ItemThumb item={i} trip={trip.id} iconSize={16} />
                  <span className="tm-row-text">
                    <span className="callout">{i.title}</span>
                    <span className="caption tm-muted">{said ? `"${said}"` : itemKind(i)}</span>
                  </span>
                </button>
                <ItemLinks item={i} saved={links} />
                <RateMine item={i} trip={trip.id} you={trip.you} onChanged={onChanged} />
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="callout tm-muted">No picks in the plan yet.</p>
      )}
      {pads.map((pad) => (
        <PadBody key={pad.id} pad={pad} entries={entries.filter((e) => e.pad === pad.id)} />
      ))}
    </article>
  );
}

function PadBody({ pad, entries }: { pad: Pad; entries: PadEntry[] }) {
  // A read-only trip: a friend's tip can't be brought in, so nothing asks your AI to.
  const readOnly = useReadOnly();
  return (
    <section className="tm-pad" aria-label={pad.name}>
      <span className="eyebrow tm-muted">{pad.name}</span>
      {pad.prompt && <p className="caption tm-muted">{pad.prompt}</p>}
      {pad.closed ? (
        <p className="callout tm-muted">Closed. {pad.for} can't add more.</p>
      ) : readOnly ? (
        // Its page takes no tip while the trip is read-only (the server refuses one), so its link isn't offered.
        <p className="callout tm-muted">The trip is read-only, so {pad.for} can't add more for now.</p>
      ) : (
        <CopyLink url={pad.link} />
      )}
      {entries.length === 0 ? (
        <p className="callout tm-muted">Nothing from {pad.for} yet.</p>
      ) : (
        <ul className="tm-pad-entries">
          {entries.map((e) => (
            <li key={e.id} className={e.taken ? "is-taken" : ""}>
              <p className="quote">{e.text}</p>
              {e.links.map((url) => (
                <a key={url} className="caption tm-link" href={url} target="_blank" rel="noreferrer noopener nofollow">
                  {url}
                </a>
              ))}
              <span className="caption tm-muted">
                {e.taken
                  ? "In the plan"
                  : readOnly
                    ? `New from ${e.from}`
                    : `New from ${e.from}: ask your AI to bring it in`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
